/* Shared-photo handoff into the existing, customer-confirmed product editors. */
(function (window, document) {
  'use strict';
  if (window.GioStudioImport) return;
  var API = 'https://ai-api.giftinone.com';
  var adapter = null;
  var running = false;
  var generation = 0;
  var banner = null;
  var designId = new URL(window.location.href).searchParams.get('gio_design');

  function waitFor(check, message, timeout) {
    var start = Date.now();
    return new Promise(function (resolve, reject) {
      function poll() {
        try {
          var value = check();
          if (value) return resolve(value);
        } catch (error) { return reject(error); }
        if (Date.now() - start >= (timeout || 15000)) return reject(new Error(message));
        window.setTimeout(poll, 60);
      }
      poll();
    });
  }

  // The normalized rectangle always refers to the upright full-resolution photo.
  // If the product's real aspect ratio differs, retain the saved center and fit
  // inside its rectangle. Never stretch a face or apply camera rotation to print.
  function pixelCrop(crop, imageWidth, imageHeight, aspectRatio) {
    var w = Number(imageWidth), h = Number(imageHeight);
    if (!(w > 0 && h > 0) || !crop) throw new Error('The saved photo crop is unavailable.');
    var values = ['x', 'y', 'width', 'height'].map(function (key) { return Number(crop[key]); });
    if (!values.every(Number.isFinite) || values[2] <= 0 || values[3] <= 0) {
      throw new Error('The saved photo crop is invalid. Please return to your gift previews.');
    }
    var x = Math.max(0, Math.min(1, values[0]));
    var y = Math.max(0, Math.min(1, values[1]));
    var cw = Math.max(0, Math.min(1 - x, values[2])) * w;
    var ch = Math.max(0, Math.min(1 - y, values[3])) * h;
    if (cw < 1 || ch < 1) throw new Error('The saved crop is too small. Please adjust it in your gift previews.');
    var cx = x * w + cw / 2, cy = y * h + ch / 2;
    var ratio = Number(aspectRatio);
    if (ratio > 0 && Number.isFinite(ratio)) {
      if (cw / ch > ratio) cw = ch * ratio;
      else ch = cw / ratio;
    }
    return { x: Math.max(0, Math.min(w - cw, cx - cw / 2)),
      y: Math.max(0, Math.min(h - ch, cy - ch / 2)), width: cw, height: ch };
  }

  function fabricPlacement(crop, imageWidth, imageHeight, canvasWidth, canvasHeight) {
    var rect = pixelCrop(crop, imageWidth, imageHeight, canvasWidth / canvasHeight);
    var scale = canvasWidth / rect.width;
    return { left: (imageWidth / 2 - rect.x) * scale,
      top: (imageHeight / 2 - rect.y) * scale, scaleX: scale, scaleY: scale,
      angle: 0, flipX: false, flipY: false, originX: 'center', originY: 'center' };
  }

  function clearNotice() {
    if (banner) banner.remove();
    banner = null;
  }

  function useManualPhoto() {
    // A new local selection supersedes a pending/retriable Studio handoff.
    generation += 1;
    designId = null;
    clearNotice();
    var location = new URL(window.location.href);
    if (location.searchParams.has('gio_design')) {
      location.searchParams.delete('gio_design');
      window.history.replaceState(window.history.state, '', location.href);
    }
  }

  function notice(message, retry) {
    if (!banner || !banner.isConnected) {
      banner = document.createElement('div');
      banner.className = 'gio-studio-import-status';
      banner.setAttribute('role', 'status');
      banner.setAttribute('aria-live', 'polite');
      banner.style.cssText = 'display:block;position:relative;box-sizing:border-box;width:100%;max-width:100%;min-width:0;height:auto;flex:0 0 100%;clear:both;padding:0;margin:12px 0;color:#545454;font-family:inherit;font-size:14px;line-height:1.5;text-align:left;white-space:normal;overflow-wrap:anywhere;';
      var trigger = document.getElementById('customButton') || document.getElementById('inputImage');
      // The layer editor's .uploadfont.view-img is a 46px-high flex row.
      // A multi-line status must precede that row, never become one of its items.
      var anchor = document.querySelector('.giftinone-ai-control')
        || (trigger && (trigger.closest('[data-gio-legacy-customizer], .uploadfont.view-img') || trigger));
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(banner, anchor);
      else (document.querySelector('main') || document.body).appendChild(banner);
    }
    banner.replaceChildren();
    var text = document.createElement('div');
    text.textContent = message;
    banner.appendChild(text);
    if (retry) {
      var actions = document.createElement('div');
      actions.style.cssText = 'display:flex;flex-wrap:wrap;align-items:stretch;gap:8px;box-sizing:border-box;max-width:100%;margin-top:12px;';
      var buttonStyle = 'appearance:none;display:inline-flex;align-items:center;justify-content:center;position:static;box-sizing:border-box;flex:0 1 auto;max-width:100%;min-width:0;min-height:42px;height:auto;margin:0;padding:8px 12px;border:1px solid #d9d9d9;border-radius:6px;background:#f7f7f9;color:#252525;font:inherit;line-height:1.4;text-align:center;text-decoration:none;white-space:normal;overflow-wrap:anywhere;cursor:pointer;';
      var button = document.createElement('button');
      button.type = 'button';
      button.textContent = 'Retry photo import';
      button.style.cssText = buttonStyle;
      button.addEventListener('click', start);
      actions.appendChild(button);
      banner.appendChild(actions);
    }
  }

  async function fetchWithTimeout(url, options, timeout) {
    var controller = new AbortController();
    var timer = window.setTimeout(function () { controller.abort(); }, timeout || 30000);
    try {
      var response = await fetch(url, Object.assign({}, options || {}, { signal: controller.signal }));
      // Read the body before clearing the timeout (slow/failed source downloads).
      if (options && options.gioBlob) {
        if (!response.ok) throw new Error('Your photo could not be downloaded. Please retry.');
        return await response.blob();
      }
      var data = await response.json().catch(function () { return {}; });
      if (!response.ok || data.ok === false) {
        if (response.status === 401 || response.status === 403) throw new Error('This photo belongs to another browser session. Please return to your gift previews and upload it again.');
        if (response.status === 404 || response.status === 410) throw new Error('This saved photo has expired. Please return to your gift previews and upload it again.');
        throw new Error('The saved photo could not be loaded. Please retry.');
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('Loading your photo took too long. Check your connection and retry.');
      throw error;
    } finally { window.clearTimeout(timer); }
  }

  function currentVariant() {
    if (!adapter) return '';
    var idNode = document.getElementById('productButtonId');
    var cartButton = idNode ? document.getElementById(idNode.textContent.trim()) : null;
    var form = cartButton && (cartButton.form || cartButton.closest('form'));
    var input = form && form.querySelector('[name="id"]');
    if (!input) input = document.querySelector('product-info form[data-type="add-to-cart-form"] [name="id"], product-info form[action*="/cart/add"] [name="id"]');
    return String(input && input.value || adapter.variantId || '');
  }

  async function selectStandardMode() {
    var root = document.querySelector('.giftinone-ai-control');
    if (!root) return;
    await waitFor(function () {
      var restore = root.querySelector('[data-ai-restore-status]');
      return root.dataset.aiInitialized === 'true' && root.dataset.aiState !== 'booting'
        && (!restore || restore.hidden || root.dataset.aiState === 'configuration-error' || root.dataset.aiState === 'fallback');
    }, 'The photo editor is still loading. Please retry in a moment.', 25000);
    if (root.dataset.aiMode === 'regular' || root.dataset.aiState === 'fallback') return;
    var button = root.querySelector('[data-ai-mode-standard]');
    if (!button || button.disabled) throw new Error('Select Standard photo upload, then retry importing your photo.');
    button.click();
    await waitFor(function () { return root.dataset.aiMode === 'regular'; },
      'Standard photo upload could not be selected. Please select it and retry.', 15000);
  }

  async function start() {
    if (!designId || running) return;
    running = true;
    var run = ++generation;
    try {
      notice('Loading your saved photo and crop…', false);
      if (!/^[a-f0-9-]{36}$/i.test(designId)) throw new Error('This saved photo link is invalid. Please return to your gift previews.');
      var data = await fetchWithTimeout(API + '/studio/designs/' + encodeURIComponent(designId), { credentials: 'include', cache: 'no-store' });
      await waitFor(function () { return adapter; }, 'This product does not support importing a saved photo. Please use its normal upload option.', 12000);
      var design = data.design, photo = data.photo;
      if (!design || !photo || !photo.url || !photo.photoId) throw new Error('This saved photo is incomplete. Please return to your gift previews.');
      if (String(design.productId) !== String(adapter.productId) || (design.handle && design.handle !== adapter.handle)) {
        throw new Error('This photo preview belongs to a different product. Please open that product from your gift previews.');
      }
      await waitFor(function () { return currentVariant() === String(design.variantId); },
        'Your selected product option has changed. Return to your gift previews and choose this option again.', 6000);
      var available = adapter.availableVariants || [];
      if (available.indexOf(String(design.variantId)) === -1) throw new Error('This option is sold out. Please select another option in your gift previews.');
      await selectStandardMode();
      var blob = await fetchWithTimeout(photo.url, { credentials: 'omit', cache: 'no-store', gioBlob: true }, 90000);
      if (!blob.size) throw new Error('The saved photo is empty. Please upload it again.');
      var type = String(photo.contentType || blob.type || '').split(';')[0];
      if (!/^image\/(jpeg|png|webp)$/.test(type)) throw new Error('This saved photo format cannot be imported. Please upload a JPG, PNG, or WebP photo.');
      var file = new File([blob], photo.name || ('photo.' + (type === 'image/jpeg' ? 'jpg' : type.split('/')[1])), { type: type });
      Object.defineProperty(file, 'gioStudioPhotoId', { value: String(photo.photoId), enumerable: false, writable: false });
      if (run !== generation) return;
      if (currentVariant() !== String(design.variantId)) throw new Error('Your selected product option changed while the photo was loading. Please retry with the original option.');
      var result = await adapter.importPhoto(file, design, photo);
      if (run !== generation) return;
      notice(result && result.message || 'Your photo is ready. Select Custom Now to review and confirm it.', false);
    } catch (error) {
      if (run === generation) notice(error.message || 'Your photo could not be imported. Retry, or use Custom Now to choose a photo.', true);
    } finally { running = false; }
  }

  window.GioStudioImport = { register: function (value) { adapter = value; },
    waitFor: waitFor, pixelCrop: pixelCrop, fabricPlacement: fabricPlacement, currentVariant: currentVariant,
    clearNotice: clearNotice, useManualPhoto: useManualPhoto };
  if (designId) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else window.setTimeout(start, 0);
  }
})(window, document);
